/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */

/**
 * Default options for the NumberFormatter
 */
const DefaultNumberFormatterOptions = {
  useGrouping: false
};
/**
 * Default value for shorthand labels.
 */

export const defaultShorthandLabels = false;
/**
 * The maximum decimal places.
 */

const maximumDecimalPlaces = 10;
/**
 * A utility class to format numbers
 */

export class NumberFormatter {
  constructor(shorthandLabels = defaultShorthandLabels, decimalPlaces) {
    // validate given value for decimal places
    if (decimalPlaces < 0) {
      decimalPlaces = 0; // eslint-disable-next-line

      console.warn(`the provided value for decimalPlaces is less than 0. The value for decimalPlaces is set to ${decimalPlaces}`);
    } else if (decimalPlaces > maximumDecimalPlaces) {
      decimalPlaces = maximumDecimalPlaces; // eslint-disable-next-line

      console.warn(`the provided value for decimalPlaces is greater than ${maximumDecimalPlaces}. The value for decimalPlaces is set to ${decimalPlaces}`);
    }

    this._shorthandLabels = shorthandLabels;
    this._decimalPlaces = decimalPlaces;
  }
  /**
   * Creates the default {@link NumberFormatter} that is used to format numeric values found in charts.
   */


  static CREATE_DEFAULT() {
    return new NumberFormatter();
  }

  /**
   * Formats the given value using the given locale.
   *
   * @param value - the value to be formatted
   * @param locale - the locale to be used; skip to use the host default locale
   *
   */
  format(value, locale) {
    value += 0.0; // avoid -0.0

    if (this._shorthandLabels) {
      return this._formatWithShorthand(value, locale);
    } else {
      return this._formatWithoutShorthand(value, locale);
    }
  }

  _formatWithoutShorthand(value, locale) {
    const intlNumberFormat = IntlNumberFormatCache.GET_INTL_NUMBER_FORMAT(this._decimalPlaces, locale);
    return intlNumberFormat.format(value);
  }
  /**
   * Formats with shorthand if possible.
   */


  _formatWithShorthand(value, locale) {
    if (value === 0) {
      return this._formatWithoutShorthand(value, locale);
    }

    const absValue = Math.abs(value);
    const log10 = Math.log10(absValue);
    const floorLog10 = Math.floor(log10);
    const shorthandIndex = Math.floor(floorLog10 / 3) * 3;

    if (shorthandIndex === 0 || shorthandIndex < 3 && shorthandIndex > -4) {
      return this._formatWithoutShorthand(value, locale);
    }

    const scaledValue = value / 10 ** shorthandIndex;

    const siPrefix = NumberFormatter._siPrefixes.get(shorthandIndex);

    return this._formatWithoutShorthand(scaledValue, locale) + siPrefix;
  }
  /**
   * Returns number with max decimalPlaces. Trailing 0 will be removed.
   * e.g. 1.0000 --> 1
   *
   * @param val - the number to trim
   * @param decimalPlaces - max decimal places for the given number
   * @returns the trimmed value as string
   */


  static TRIM_FLOAT(val, decimalPlaces) {
    return parseFloat('' + val.toFixed(decimalPlaces)) + '';
  }

}
/*
 * Creating a new Intl.NumberFormat object is very expensive. Hence we cache the created instances.
 * Every time the locale changes we clear the cache. The locale is supposed to change very rarely
 * and is the same for all charts of the application.
 */

NumberFormatter._siPrefixes = new Map([[24, 'Y'], [21, 'Z'], [18, 'E'], [15, 'P'], [12, 'T'], [9, 'G'], [6, 'M'], [3, 'k'], [0, ''], [-3, 'm'], [-6, 'µ'], [-9, 'n'], [-12, 'p'], [-15, 'f'], [-18, 'a'], [-21, 'z'], [-24, 'y']]);

class IntlNumberFormatCache {
  /**
   * Retrieves a Intl.NumberFormat from the cache.
   */
  static GET_INTL_NUMBER_FORMAT(decimalPlaces, locale) {
    if (IntlNumberFormatCache._lastLocale !== locale) {
      IntlNumberFormatCache._lastLocale = locale;
      IntlNumberFormatCache._intlNumberFormatCache = new Array();
    }

    let maxFractionDigits = maximumDecimalPlaces;
    let minFractionDigits = 0;
    let indexToStore;

    if (decimalPlaces === undefined) {
      indexToStore = maximumDecimalPlaces + 1;
    } else {
      maxFractionDigits = decimalPlaces;
      minFractionDigits = decimalPlaces;
      indexToStore = decimalPlaces;
    }

    let intlNumberFormat = IntlNumberFormatCache._intlNumberFormatCache[indexToStore];

    if (intlNumberFormat === undefined) {
      intlNumberFormat = new Intl.NumberFormat(locale, { ...DefaultNumberFormatterOptions,
        maximumFractionDigits: maxFractionDigits,
        minimumFractionDigits: minFractionDigits
      });
      IntlNumberFormatCache._intlNumberFormatCache[indexToStore] = intlNumberFormat;
    }

    return intlNumberFormat;
  }

}

IntlNumberFormatCache._lastLocale = '';
IntlNumberFormatCache._intlNumberFormatCache = new Array();
